import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton, withStyles } from 'vopay';
import { BodyText } from 'UI';
import { ClientAccountList } from 'shared/clientAccountList';
import useAccountBearer from '@/components/Entities/hooks/useApi/useAccountBearer';
import AccountScopedApiProvider from './AccountScopedApiProvider';
import useAccountClientAccountsHostProps from './useAccountClientAccountsHostProps';
import styles from './ClientAccountsTab.module.scss';

const cn = withStyles(styles);

/**
 * The account page's Client Accounts tab (spec 5.6.6, ID-13352): the shared Client Account List, scoped
 * to this account by its own bearer. The cards the design shows above the table are out of scope for
 * this ticket; the list is chrome-less and the tab card is its container.
 */
export default function ClientAccountsTab() {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.Account.Tabs' });
  const { accountId } = useParams<{ accountId: string }>();
  const { bearer, hasSettled } = useAccountBearer(accountId);
  const hostProps = useAccountClientAccountsHostProps(accountId);

  // On a deep link the token is looked up after mount; the list fetches on mount, so it must not
  // render until the token is there to scope that fetch.
  if (!bearer && !hasSettled) return <Skeleton height={320} data-testid="clientAccountsTabLoading" />;

  // The lookup has answered and there is no token to scope the list with — see TransactionsTab.
  if (!bearer) {
    return (
      <BodyText data-testid="clientAccountsTabUnavailable">
        {t('unavailable', { defaultValue: "This account's data could not be loaded." })}
      </BodyText>
    );
  }

  return (
    <div className={cn('tab')}>
      <AccountScopedApiProvider bearer={bearer}>
        <ClientAccountList {...hostProps} />
      </AccountScopedApiProvider>
    </div>
  );
}
